<script lang="ts" setup>
import { ref, onMounted, computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAxios } from '@/composables/useAxios'
import { handleAxiosError } from '@/composables/useAxiosError'
import { usePagination } from '@/composables'
import { STATUS_CODES } from '@/constants'
import { DateTime } from 'luxon'
import { AdminK8sTenantListItem, type PaginatedResponse } from '@/types'
import BaseDashboardPage from '@/components/base/BaseDashboardPage.vue'

defineOptions({ name: 'AdminK8sHome' })

const { t } = useI18n()
const router = useRouter()

const loading = ref(true)
const tenants = ref<AdminK8sTenantListItem[]>([])
const statusFilter = ref<string>('all')
const searchQuery = ref('')
const searchDebounce = ref<ReturnType<typeof setTimeout> | null>(null)

const pagination = usePagination({ initialItemsPerPage: 50 })

const statusOptions = computed(() => [
  { value: 'all', title: t('dashboard.admin.kubernetes.status.all') },
  { value: 'active', title: t('dashboard.admin.kubernetes.status.active') },
  { value: 'provisioning', title: t('dashboard.admin.kubernetes.status.provisioning') },
  { value: 'failed', title: t('dashboard.admin.kubernetes.status.failed') },
  { value: 'deprovisioned', title: t('dashboard.admin.kubernetes.status.deprovisioned') }
])

async function fetchTenants() {
  loading.value = true
  const { limit, offset } = pagination.buildQueryParams()

  let url = `/admin/k8s/tenants?limit=${limit}&offset=${offset}`
  if (statusFilter.value !== 'all') {
    url += `&status=${statusFilter.value}`
  }
  if (searchQuery.value.trim()) {
    url += `&search=${encodeURIComponent(searchQuery.value.trim())}`
  }

  try {
    const res = await useAxios<null, PaginatedResponse<AdminK8sTenantListItem>>(
      'GET',
      url,
      true
    )

    if (res && res.status === STATUS_CODES.OK && res.data) {
      const validate = AdminK8sTenantListItem.array().safeParse(res.data.data)
      if (validate.success) {
        tenants.value = validate.data
      } else {
        tenants.value = res.data.data as AdminK8sTenantListItem[]
      }
      pagination.setTotal(res.data.total)
    }
  } catch (err) {
    handleAxiosError(err as Error, { logPrefix: 'AdminK8sHome' })
  } finally {
    loading.value = false
  }
}

watch(() => pagination.page.value, () => {
  fetchTenants()
})

watch(statusFilter, () => {
  pagination.goToPage(1)
  fetchTenants()
})

watch(searchQuery, () => {
  if (searchDebounce.value) {
    clearTimeout(searchDebounce.value)
  }
  searchDebounce.value = setTimeout(() => {
    pagination.goToPage(1)
    fetchTenants()
  }, 300)
})

const showingFrom = computed(() => ((pagination.page.value - 1) * pagination.itemsPerPage.value) + 1)
const showingTo = computed(() => Math.min(showingFrom.value + tenants.value.length - 1, pagination.total.value))

function formatDate(ts: number | null | undefined): string {
  if (!ts) return '-'
  return DateTime.fromSeconds(ts).toFormat('dd.MM.yyyy')
}

function getStatusClass(status: string): string {
  switch (status) {
    case 'active': return 'status-active'
    case 'provisioning': return 'status-provisioning'
    case 'failed': return 'status-failed'
    case 'deprovisioning': return 'status-deprovisioning'
    case 'deprovisioned': return 'status-deprovisioned'
    default: return ''
  }
}

function getStatusLabel(status: string): string {
  const opt = statusOptions.value.find(s => s.value === status)
  return opt?.title || status
}

function navigateToDetail(accountId: number) {
  router.push({ name: 'dashboardAdminK8sTenantDetail', params: { id: accountId } })
}

onMounted(() => {
  fetchTenants()
})
</script>

<template>
  <BaseDashboardPage
    :title="$t('dashboard.admin.kubernetes.title')"
    :subtitle="$t('dashboard.admin.kubernetes.subtitle')"
    icon="mdi-kubernetes"
    icon-color="cyan"
  >
    <template #actions>
      <button class="btn-text" @click="router.push({ name: 'dashboardAdminK8sUsage' })">
        <v-icon icon="mdi-chart-bar" size="16" />
        {{ $t('dashboard.admin.kubernetes.usageOverview.title') }}
      </button>
      <button class="btn-text" @click="router.push({ name: 'dashboardAdminK8sDomains' })">
        <v-icon icon="mdi-web" size="16" />
        {{ $t('dashboard.admin.kubernetes.domains.title') }}
      </button>
    </template>

    <!-- Filters -->
    <div class="filters-bar">
      <v-text-field
        v-model="searchQuery"
        prepend-inner-icon="mdi-magnify"
        :placeholder="$t('dashboard.admin.kubernetes.searchPlaceholder')"
        variant="outlined"
        density="compact"
        hide-details
        class="search-input"
      />
      <div class="status-filters">
        <button
          v-for="opt in statusOptions"
          :key="opt.value"
          class="filter-chip"
          :class="{ active: statusFilter === opt.value }"
          @click="statusFilter = opt.value"
        >
          {{ opt.title }}
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="state-box">
      <v-progress-circular indeterminate size="32" width="3" color="#00B0FF" />
    </div>

    <!-- Empty State -->
    <div v-else-if="tenants.length === 0" class="state-box">
      <v-icon icon="mdi-kubernetes" size="48" class="empty-icon" />
      <p class="empty-title">{{ $t('dashboard.admin.kubernetes.empty.title') }}</p>
      <p class="empty-hint">{{ $t('dashboard.admin.kubernetes.empty.description') }}</p>
    </div>

    <!-- Tenants Table -->
    <div v-else class="section-card">
      <div class="data-table">
        <div class="table-header">
          <span class="col">{{ $t('dashboard.admin.kubernetes.table.namespace') }}</span>
          <span class="col">{{ $t('dashboard.admin.kubernetes.table.status') }}</span>
          <span class="col">{{ $t('dashboard.admin.kubernetes.table.account') }}</span>
          <span class="col">{{ $t('dashboard.admin.kubernetes.table.product') }}</span>
          <span class="col">{{ $t('dashboard.admin.kubernetes.table.provisioned') }}</span>
          <span class="col"></span>
        </div>
        <div
          v-for="tenant in tenants"
          :key="tenant.id"
          class="table-row clickable"
          @click="navigateToDetail(tenant.account_id)"
        >
          <span class="col">
            <span class="mono">{{ tenant.namespace }}</span>
          </span>
          <span class="col">
            <span class="status-chip" :class="getStatusClass(tenant.status)">
              {{ getStatusLabel(tenant.status) }}
            </span>
          </span>
          <span class="col account-cell">
            <span class="account-email">{{ tenant.account_email }}</span>
            <span class="account-id">#{{ tenant.account_id }}</span>
          </span>
          <span class="col product-cell">{{ tenant.product_name || '-' }}</span>
          <span class="col date-cell">{{ formatDate(tenant.provisioned_at) }}</span>
          <span class="col action-col">
            <v-icon icon="mdi-chevron-right" size="18" />
          </span>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <div v-if="pagination.total.value > 0 && !loading" class="pagination-controls">
      <div class="pagination-info">
        {{ $t('dashboard.admin.kubernetes.pagination.showing', { from: showingFrom, to: showingTo, total: pagination.total.value }) }}
      </div>
      <div class="pagination-buttons">
        <button class="pagination-btn" :disabled="pagination.page.value === 1" @click="pagination.goToPage(1)">
          <v-icon icon="mdi-chevron-double-left" size="18" />
        </button>
        <button class="pagination-btn" :disabled="pagination.page.value === 1" @click="pagination.prevPage()">
          <v-icon icon="mdi-chevron-left" size="18" />
        </button>
        <span class="page-indicator">{{ pagination.page.value }} / {{ pagination.totalPages.value }}</span>
        <button class="pagination-btn" :disabled="pagination.page.value === pagination.totalPages.value" @click="pagination.nextPage()">
          <v-icon icon="mdi-chevron-right" size="18" />
        </button>
        <button class="pagination-btn" :disabled="pagination.page.value === pagination.totalPages.value" @click="pagination.goToPage(pagination.totalPages.value)">
          <v-icon icon="mdi-chevron-double-right" size="18" />
        </button>
      </div>
    </div>
  </BaseDashboardPage>
</template>

<style lang="scss" scoped>
.filters-bar {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
}

.search-input {
  max-width: 280px;
  flex: 1;
}

.status-filters {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.filter-chip {
  padding: 6px 12px;
  background: rgba(120, 163, 255, 0.08);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 8px;
  color: rgba(200, 218, 255, 0.8);
  font-size: 0.82rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(120, 163, 255, 0.15);
  }

  &.active {
    background: rgba(0, 176, 255, 0.15);
    border-color: rgba(0, 176, 255, 0.3);
    color: #00B0FF;
  }
}

.btn-text {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: rgba(120, 163, 255, 0.08);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 8px;
  color: rgba(200, 218, 255, 0.8);
  font-size: 0.82rem;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background: rgba(120, 163, 255, 0.15);
  }
}

.section-card {
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(120, 163, 255, 0.08);
  border-radius: 16px;
  padding: 0;
  overflow: hidden;
}

.data-table {
  overflow-x: auto;
}

.table-header,
.table-row {
  display: grid;
  grid-template-columns: 2fr 1fr 2fr 1.5fr 1fr 40px;
  gap: 12px;
  align-items: center;
  padding: 10px 16px;
  min-width: 860px;
}

.table-header {
  border-bottom: 1px solid rgba(120, 163, 255, 0.1);
  font-size: 0.75rem;
  font-weight: 600;
  color: rgba(200, 218, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.table-row {
  border-bottom: 1px solid rgba(120, 163, 255, 0.05);
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.85);
  transition: background 0.15s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: rgba(120, 163, 255, 0.04);
  }

  &.clickable {
    cursor: pointer;
  }
}

.mono {
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
  font-size: 0.85rem;
  font-weight: 500;
}

.status-chip {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 0.78rem;
  font-weight: 500;
}

.status-active {
  background: rgba(129, 199, 132, 0.1);
  border: 1px solid rgba(129, 199, 132, 0.3);
  color: #81c784;
}

.status-provisioning {
  background: rgba(255, 183, 77, 0.1);
  border: 1px solid rgba(255, 183, 77, 0.3);
  color: #ffb74d;
}

.status-failed {
  background: rgba(244, 67, 54, 0.1);
  border: 1px solid rgba(244, 67, 54, 0.3);
  color: #ef5350;
}

.status-deprovisioning {
  background: rgba(255, 183, 77, 0.1);
  border: 1px solid rgba(255, 183, 77, 0.3);
  color: #ffb74d;
}

.status-deprovisioned {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: rgba(200, 218, 255, 0.5);
}

.account-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.account-email {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.8);
}

.account-id {
  font-size: 0.75rem;
  color: rgba(200, 218, 255, 0.5);
}

.product-cell {
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.7);
}

.date-cell {
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.6);
  white-space: nowrap;
}

.action-col {
  text-align: right;
  color: rgba(200, 218, 255, 0.4);
}

.state-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 48px 24px;
  text-align: center;
}

.empty-icon {
  color: rgba(200, 218, 255, 0.25);
  margin-bottom: 12px;
}

.empty-title {
  font-size: 1rem;
  font-weight: 500;
  color: rgba(200, 218, 255, 0.6);
  margin: 0 0 4px;
}

.empty-hint {
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.4);
  margin: 0;
}

.pagination-controls {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  margin-top: 16px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(120, 163, 255, 0.08);
  border-radius: 12px;
}

.pagination-info {
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.6);
}

.pagination-buttons {
  display: flex;
  align-items: center;
  gap: 8px;
}

.pagination-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 8px;
  color: rgba(200, 218, 255, 0.7);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: rgba(0, 176, 255, 0.1);
    border-color: rgba(0, 176, 255, 0.25);
    color: #00B0FF;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

.page-indicator {
  font-size: 0.9rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
  padding: 0 12px;
  min-width: 80px;
  text-align: center;
}

@media (max-width: 768px) {
  .filters-bar {
    flex-direction: column;
    align-items: stretch;
  }

  .search-input {
    max-width: 100%;
  }

  .pagination-controls {
    flex-direction: column;
    gap: 12px;
  }
}
</style>
